import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import adminService from "../../api/services/adminService";
import SectionCard from "../../components/common/SectionCard";
import toast from "react-hot-toast";
import AutoTab from "../../components/DummyUsers/AutoTab";
import ManualTab, { INITIAL_FORM } from "../../components/DummyUsers/ManualTab";
import CredentialsTable from "../../components/DummyUsers/CredentialsTable";
import { useAbortableAsyncEffect } from "../../hooks/useAbortableAsyncEffect";
import { isRequestAbortError } from "../../utils/isRequestAbortError";
import { buildFullContactNo, getNationalLengthLimits } from "../../data/phoneDialCodes";

const TAB_AUTO = "auto";
const TAB_MANUAL = "manual";
const CRED_LIMIT = 20;

const DummyUsers = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(TAB_AUTO);

  const [autoCount, setAutoCount] = useState(5);
  const [autoGender, setAutoGender] = useState("");
  const [autoCountry, setAutoCountry] = useState(null);
  const [autoLoading, setAutoLoading] = useState(false);
  const [lastAutoResult, setLastAutoResult] = useState([]);

  const [manualForm, setManualForm] = useState(INITIAL_FORM);
  const [manualLoading, setManualLoading] = useState(false);

  const [credentials, setCredentials] = useState([]);
  const [credentialsLoading, setCredentialsLoading] = useState(false);
  const [credPage, setCredPage] = useState(1);
  const [credTotal, setCredTotal] = useState(0);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [csvDownloading, setCsvDownloading] = useState(false);
  const credGenRef = useRef(0);

  const fetchCredentials = async (signal) => {
    const gen = ++credGenRef.current;
    try {
      setCredentialsLoading(true);
      const res = await adminService.getDummyUserCredentials(
        { page: credPage, limit: CRED_LIMIT },
        signal ? { signal } : {}
      );
      if (gen !== credGenRef.current) return;
      setCredentials(res?.data || []);
      setCredTotal(res?.total ?? 0);
    } catch (error) {
      if (isRequestAbortError(error)) return;
      if (gen !== credGenRef.current) return;
      if (error?.status === 401) {
        toast.error("Session expired. Please log in again.");
        navigate("/login");
        return;
      }
      toast.error(error?.message || "Failed to load dummy user credentials");
    } finally {
      if (gen === credGenRef.current) setCredentialsLoading(false);
    }
  };

  useAbortableAsyncEffect((signal) => fetchCredentials(signal), [credPage]);

  const handleAutoGenerate = async () => {
    const count = Math.min(100, Math.max(1, parseInt(autoCount, 10) || 1));
    try {
      setAutoLoading(true);
      const res = await adminService.createDummyUsersAuto(
        count,
        autoGender || undefined,
        autoCountry ? autoCountry.name : undefined
      );
      setLastAutoResult(res?.users || []);
      toast.success(res?.message || `${count} user(s) created`);
      fetchCredentials();
    } catch (error) {
      if (error?.status === 401) {
        toast.error("Session expired. Please log in again.");
        navigate("/login");
        return;
      }
      toast.error(error?.message || "Failed to create dummy users");
    } finally {
      setAutoLoading(false);
    }
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    const email = (manualForm.email || "").trim();
    if (!email.endsWith("@naseebi.com")) {
      toast.error("Email must end with @naseebi.com");
      return;
    }
    if (!manualForm.password) {
      toast.error("Password is required");
      return;
    }
    const dial = manualForm.phoneDial || "+92";
    const national = String(manualForm.phoneNational || "").replace(/\D/g, "");
    let contactNo = undefined;
    if (national.length > 0) {
      const { min, max } = getNationalLengthLimits(dial);
      if (national.length < min || national.length > max) {
        const range = min === max ? `exactly ${min} digits` : `${min}–${max} digits`;
        toast.error(`Contact number must be ${range} after the country code (or leave it empty).`);
        return;
      }
      contactNo = buildFullContactNo(dial, national);
    }
    try {
      setManualLoading(true);
      await adminService.createDummyUserManual({
        firstName: manualForm.firstName || "User",
        lastName: manualForm.lastName || "Dummy",
        email,
        password: manualForm.password,
        gender: manualForm.gender,
        contactNo,
      });
      toast.success("Dummy user created successfully");
      setManualForm(INITIAL_FORM);
      fetchCredentials();
    } catch (error) {
      if (error?.status === 401) {
        toast.error("Session expired. Please log in again.");
        navigate("/login");
        return;
      }
      toast.error(error?.message || "Failed to create dummy user");
    } finally {
      setManualLoading(false);
    }
  };

  const toggleSelectRow = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllOnPage = (checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      credentials.forEach((r) => (checked ? next.add(r.id) : next.delete(r.id)));
      return next;
    });
  };

  const buildCsvAndDownload = (rows, filename) => {
    const escape = (v) => {
      const s = v == null ? "" : String(v);
      return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = "Email,Password,User ID,Gender,Created At";
    const body = rows
      .map((r) => [r.email, r.password, r.userId, r.gender ?? "", r.createdAt ? new Date(r.createdAt).toISOString() : ""].map(escape).join(","))
      .join("\n");
    const blob = new Blob([header + "\n" + body], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "dummy-users-credentials.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadCsv = async (type) => {
    try {
      setCsvDownloading(true);
      const ids = type === "selected" ? Array.from(selectedIds) : null;
      if (type === "selected" && ids.length === 0) {
        toast.error("Select at least one row to download.");
        return;
      }
      const res = await adminService.getDummyUserCredentialsExport(ids);
      const rows = res?.data || [];
      if (rows.length === 0) {
        toast.error("No credentials to export.");
        return;
      }
      const filename = type === "selected" ? `dummy-users-selected-${rows.length}.csv` : "dummy-users-all.csv";
      buildCsvAndDownload(rows, filename);
      toast.success(`Downloaded ${rows.length} credential(s) as CSV.`);
    } catch (error) {
      if (error?.status === 401) {
        toast.error("Session expired. Please log in again.");
        navigate("/login");
        return;
      }
      toast.error(error?.message || "Failed to export CSV");
    } finally {
      setCsvDownloading(false);
    }
  };

  const setCredPageBounded = (p) => {
    const totalPages = Math.ceil(credTotal / CRED_LIMIT) || 1;
    setCredPage(Math.max(1, Math.min(totalPages, p)));
  };

  return (
    <div className="w-full p-4 overflow-x-hidden">
      <SectionCard
        title="Dummy Users"
        subtitle="Create dummy users with @naseebi.com emails. Auto: generate random users. Manual: add one user with your data. All credentials are stored below."
      >
        <div className="flex gap-2 border-b border-gray-200 mb-4">
          <button
            type="button"
            onClick={() => setActiveTab(TAB_AUTO)}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
              activeTab === TAB_AUTO ? "border-red-500 text-red-600 bg-red-50" : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            Auto
          </button>
          <button
            type="button"
            onClick={() => setActiveTab(TAB_MANUAL)}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
              activeTab === TAB_MANUAL ? "border-red-500 text-red-600 bg-red-50" : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            Manual
          </button>
        </div>

        {activeTab === TAB_AUTO && (
          <AutoTab
            count={autoCount}
            onCountChange={setAutoCount}
            gender={autoGender}
            onGenderChange={setAutoGender}
            country={autoCountry}
            onCountryChange={setAutoCountry}
            onGenerate={handleAutoGenerate}
            loading={autoLoading}
            lastResult={lastAutoResult}
          />
        )}

        {activeTab === TAB_MANUAL && (
          <ManualTab
            form={manualForm}
            onChange={setManualForm}
            onSubmit={handleManualSubmit}
            loading={manualLoading}
          />
        )}

        <CredentialsTable
          credentials={credentials}
          loading={credentialsLoading}
          page={credPage}
          limit={CRED_LIMIT}
          total={credTotal}
          selectedIds={selectedIds}
          onToggleRow={toggleSelectRow}
          onSelectAllPage={selectAllOnPage}
          onDownloadSelected={() => handleDownloadCsv("selected")}
          onDownloadAll={() => handleDownloadCsv("all")}
          onPageChange={setCredPageBounded}
          csvDownloading={csvDownloading}
        />
      </SectionCard>
    </div>
  );
};

export default DummyUsers;
